//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../ui/collapsible';
import { cn } from '../../../lib/utils';

interface ExecutionStep {
  step: number;
  action: string;
  description: string;
  agent: string;
  estimated_time?: string;
}

interface ExecutionPlanProps {
  agent_description?: string;
  plan_summary?: string;
  total_steps?: number;
  estimated_duration?: string;
  complexity?: 'low' | 'medium' | 'high';
  risk_level?: 'low' | 'medium' | 'high';
  steps?: ExecutionStep[];
  requires_approval?: boolean;
}

const ExecutionPlan: React.FC<ExecutionPlanProps> = ({
  total_steps = 0,
  steps = [],
}) => {
  const { t } = useTranslation();

  // Safety check - if steps is still undefined/null, use empty array
  const safeSteps = Array.isArray(steps) ? steps : [];
  const actualStepCount = total_steps || safeSteps.length;

  return (
    <div className="mt-2 rounded-lg border border-[#E0E0E0] bg-[#F5F7FA] px-4 py-3">
      <Collapsible>
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 rounded py-1 text-left hover:bg-black/[0.02] [&[data-state=open]>svg]:rotate-180">
          <span className="flex items-center gap-2 font-[Roboto] text-[13px] font-semibold text-[#1C262E]">
            {t('execution-steps', { count: actualStepCount })}
          </span>
          <ChevronDown className="size-5 shrink-0 text-[#5F6368] transition-transform duration-300" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3">
            {safeSteps.length > 0 ? (
              safeSteps.map((step, index) => (
                <div
                  key={index}
                  className={cn(
                    'rounded-md border border-gray-300 bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all hover:border-[#1976D2] hover:shadow-[0_2px_4px_rgba(25,118,210,0.1)]',
                    index < safeSteps.length - 1 && 'mb-2',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[#1976D2] text-xs font-semibold text-white">
                      {step.step || index + 1}
                    </div>

                    <div className="flex-1">
                      <p className="mb-1 font-[Roboto] text-[13px] font-medium text-[#1C262E]">
                        {step.action || t('action-item')}
                      </p>
                      <p className="font-[Roboto] text-xs leading-normal text-[#5F6368]">
                        {step.description || t('no-description-available')}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="font-[Roboto] text-xs italic text-[#5F6368]">
                {t('no-steps-available')}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default ExecutionPlan;
